import * as React from "react";

import { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { Box, Button, Card, CardActions, CardContent, CardOverflow, LinearProgress, Modal, ModalDialog, Stack, Typography } from "@mui/joy";
import { Core } from "../../utilities/core";
import InfoIcon from "@mui/icons-material/Info";
import DropZone from "./FileUpload";
import SendIcon from "@mui/icons-material/Send";
import { RootConfig, DynamicFieldSelection, FieldData, FieldValidation, SupportConfig, FileUploadStatus, Field, getSalesforceFieldName } from "./types";

import InputField from "./InputField";

import { Buffer } from "buffer";
window.Buffer = Buffer;

type NewTicketProps = {
  account?: AccountInfo;
  instance?: PublicClientApplication;
};

// Helper function to flatten all fields including those within sections
const getAllFields = (fields: Field[]): Field[] => {
  const result: Field[] = [];

  fields.forEach((field) => {
    if (field.type === "section" && field.sectionFields) {
      // Recursively get fields from sections
      result.push(...getAllFields(field.sectionFields));
    } else {
      // Add regular fields
      result.push(field);
    }
  });

  return result;
};

// Helper function to recursively find all fields that depend on a given field
const getDependentFields = (fieldName: string, allFields: Field[]): Field[] => {
  const dependents: Field[] = [];

  allFields.forEach((field) => {
    // Check if field depends on the given field via dynamicOptions
    if (field.dynamicOptions?.mappingFrom === fieldName) {
      dependents.push(field);
      // Recursively find fields that depend on this dependent field
      dependents.push(...getDependentFields(field.name, allFields));
    }

    // Check if field depends on the given field via visibilityCondition
    if (field.visibilityCondition?.from === fieldName) {
      dependents.push(field);
      // Recursively find fields that depend on this dependent field
      dependents.push(...getDependentFields(field.name, allFields));
    }
  });

  // Remove duplicates
  return Array.from(new Set(dependents));
};

export default function NewTicket({ account, instance }: NewTicketProps) {
  const [mounted, setMounted] = React.useState<boolean>(false);
  const [fields, setFields] = React.useState<RootConfig>({});
  const [fieldsLoaded, setFieldsLoaded] = React.useState<boolean>(false);

  const [dynamicFieldSelection, setDynamicFieldSelection] = React.useState<DynamicFieldSelection>({});
  const [fieldData, setFieldData] = React.useState<FieldData>({});
  const [selectedServiceTeam, setSelectedServiceTeam] = React.useState<string>();
  const [selectedFields, setSelectedFields] = React.useState<SupportConfig>();
  const [fieldValid, setFieldValid] = React.useState<FieldValidation>({});

  const [files, setFiles] = React.useState<File[]>([]);
  const [fileUploadDone, setFileUploadDone] = React.useState<FileUploadStatus>({});

  const [core, setCore] = React.useState<Core>();
  const [integrationHubUser, setIntegrationHubUser] = React.useState<any>();

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [modalResult, setModalResult] = React.useState<"ticket" | "upload" | "done" | "error">("ticket");
  const [modalErrorMessage, setModalErrorMessage] = React.useState<string>();

  const loadFieldConfigurations = async () => {
    try {
      // Load the manifest file to get list of field configuration files
      const manifestResponse = await fetch("/fields/manifest.json");
      const manifest: string[] = await manifestResponse.json();

      // Load each field configuration file
      const fieldConfigs: RootConfig = {};

      for (const filename of manifest) {
        try {
          const response = await fetch(`/fields/${filename}`);
          const config = await response.json();

          // Use the 'name' property from the config as the key
          if (config.name) {
            fieldConfigs[config.name] = config;
          }
        } catch (error) {
          console.warn(`Failed to load field configuration: ${filename}`, error);
        }
      }

      setFields(fieldConfigs);
      setFieldsLoaded(true);
    } catch (error) {
      console.error("Failed to load field configurations:", error);
      setFieldsLoaded(true); // Set to true even on error to avoid infinite loading
    }
  };

  React.useEffect(() => {
    setMounted(true);
    loadFieldConfigurations();
  }, []);

  React.useEffect(() => {
    if (mounted && account && instance) {
      new Core().initialiseCore(instance, account).then((core) => {
        setCore(core);
      });
    }
  }, [mounted, account, instance]);

  React.useEffect(() => {
    if (account && core) {
      core.invokeIntegrationHubRequest("GET", `/identity/users/${(account as AccountInfo).username}`).then((resp) => {
        setIntegrationHubUser(resp.body);
      });
    }
  }, [account, core]);

  // Field Initialization Effect - Sets up field data when service team is selected
  React.useEffect(() => {
    if (selectedServiceTeam && integrationHubUser) {
      const selectedConfig = fields[selectedServiceTeam];
      setSelectedFields(selectedConfig);
      const allFields = getAllFields(selectedConfig.fields);
      const initialFieldData = {
        ...allFields
          .filter((f) => {
            if (!f.salesforce?.fieldName) return false;

            // If no visibility condition, always include
            if (!f.visibilityCondition?.from) return true;

            // During initial load, check defaultVisibility
            return f.visibilityCondition.defaultVisibility !== false;
          })
          .reduce(
            (obj, item) => ({ ...obj, [getSalesforceFieldName(item.salesforce!)]: item.valueFrom ? integrationHubUser[item.valueFrom] : item.default || "" }),
            {}
          ),
      };
      setFieldData(initialFieldData);
      setDynamicFieldSelection({}); // Reset dynamic selections
    }
  }, [selectedServiceTeam, integrationHubUser]);

  // Cascade Field Reset Effect - Clears dependent fields when parent fields change
  const prevDynamicFieldSelectionRef = React.useRef<DynamicFieldSelection>({});
  React.useEffect(() => {
    if (!selectedFields || Object.keys(dynamicFieldSelection).length === 0) {
      prevDynamicFieldSelectionRef.current = dynamicFieldSelection;
      return;
    }

    const allFields = getAllFields(selectedFields.fields);
    const fieldsToReset = new Set<string>();
    const dynamicSelectionsToReset = new Set<string>();

    // Compare current dynamicFieldSelection with previous to find changes
    Object.keys(prevDynamicFieldSelectionRef.current).forEach((fieldName) => {
      const prevValue = prevDynamicFieldSelectionRef.current[fieldName];
      const currentValue = dynamicFieldSelection[fieldName];

      // If field value changed, find all dependent fields and reset them
      if (prevValue !== currentValue) {
        const dependentFields = getDependentFields(fieldName, allFields);

        dependentFields.forEach((depField) => {
          if (depField.salesforce?.fieldName) {
            const salesforceFieldName = getSalesforceFieldName(depField.salesforce);
            fieldsToReset.add(salesforceFieldName);
          }
          dynamicSelectionsToReset.add(depField.name);
        });
      }
    });

    // Also handle new fields that appeared in dynamicFieldSelection
    Object.keys(dynamicFieldSelection).forEach((fieldName) => {
      if (!(fieldName in prevDynamicFieldSelectionRef.current)) {
        const dependentFields = getDependentFields(fieldName, allFields);

        dependentFields.forEach((depField) => {
          if (depField.salesforce?.fieldName) {
            const salesforceFieldName = getSalesforceFieldName(depField.salesforce);
            fieldsToReset.add(salesforceFieldName);
          }
          dynamicSelectionsToReset.add(depField.name);
        });
      }
    });

    // Apply the resets if needed
    if (fieldsToReset.size > 0 || dynamicSelectionsToReset.size > 0) {
      if (fieldsToReset.size > 0) {
        setFieldData((prev) => {
          const updated = { ...prev };
          fieldsToReset.forEach((fieldName) => {
            delete updated[fieldName];
          });
          return updated;
        });
      }

      if (dynamicSelectionsToReset.size > 0) {
        setDynamicFieldSelection((prev) => {
          const updated = { ...prev };
          dynamicSelectionsToReset.forEach((fieldName) => {
            delete updated[fieldName];
          });
          return updated;
        });
      }
    }

    // Update ref for next comparison
    prevDynamicFieldSelectionRef.current = dynamicFieldSelection;
  }, [dynamicFieldSelection, selectedFields]);

  // Dynamic Field Updates Effect - Updates fields when selections change
  React.useEffect(() => {
    if (selectedServiceTeam && selectedFields && Object.keys(dynamicFieldSelection).length > 0) {
      const allFields = getAllFields(selectedFields.fields);
      let newDynamicFieldSelection = { ...dynamicFieldSelection };
      let newFieldData = { ...fieldData };
      let changed = true;

      // Keep iterating until no more auto-selections are made (cascading single-option selections)
      while (changed) {
        changed = false;

        allFields.forEach((field) => {
          if (!field.salesforce?.fieldName) return;

          // Handle visibility condition changes - clear data for hidden fields
          if (field.visibilityCondition?.from && newDynamicFieldSelection[field.visibilityCondition.from]) {
            const selectedValue = newDynamicFieldSelection[field.visibilityCondition.from];
            // Handle both single values and semicolon-separated multi-select values
            const selectedValues = selectedValue.includes(';') ? selectedValue.split(';').map(v => v.trim()) : [selectedValue];
            const isVisible = selectedValues.some(value => field.visibilityCondition!.if.includes(value));

            if (!isVisible) {
              const fieldName = getSalesforceFieldName(field.salesforce);

              if (fieldName in newFieldData) {
                // Check if the same fieldName is used by another field that is still visible
                const otherFieldWithSameName = allFields.find((otherField) => {
                  if (otherField === field) return false; // Skip the current field
                  if (!otherField.salesforce?.fieldName) return false;
                  if (getSalesforceFieldName(otherField.salesforce) !== fieldName) return false;

                  // Check if this other field is visible
                  if (!otherField.visibilityCondition?.from) return true; // Always visible

                  const otherSelectedValue = newDynamicFieldSelection[otherField.visibilityCondition.from];
                  if (!otherSelectedValue) {
                    return otherField.visibilityCondition.defaultVisibility !== false;
                  }

                  // Handle both single values and semicolon-separated multi-select values
                  const otherSelectedValues = otherSelectedValue.includes(';') ? otherSelectedValue.split(';').map(v => v.trim()) : [otherSelectedValue];
                  return otherSelectedValues.some(value => otherField.visibilityCondition!.if.includes(value));
                });

                // Only delete if no other visible field uses the same fieldName
                if (!otherFieldWithSameName) {
                  delete newFieldData[fieldName];
                  changed = true;
                }
              }
            }
          }

          // Handle auto-selection for single-option dynamic fields
          if (
            field.dynamicOptions &&
            field.dynamicOptions.mappingFrom in newDynamicFieldSelection &&
            field.dynamicOptions.mapping[newDynamicFieldSelection[field.dynamicOptions.mappingFrom]]?.length === 1
          ) {
            const autoSelectedValue = field.dynamicOptions.mapping[newDynamicFieldSelection[field.dynamicOptions.mappingFrom]][0];
            const fieldName = getSalesforceFieldName(field.salesforce);

            if (newFieldData[fieldName] !== autoSelectedValue) {
              newFieldData[fieldName] = autoSelectedValue;
              newDynamicFieldSelection[field.name] = autoSelectedValue;
              changed = true;
            }
          }
        });
      }

      // Only update state if something actually changed
      if (JSON.stringify(newFieldData) !== JSON.stringify(fieldData)) {
        setFieldData(newFieldData);
      }

      if (JSON.stringify(newDynamicFieldSelection) !== JSON.stringify(dynamicFieldSelection)) {
        setDynamicFieldSelection(newDynamicFieldSelection);
      }
    }
  }, [dynamicFieldSelection, selectedServiceTeam, selectedFields]);

  React.useEffect(() => {
    if (modalOpen && files.length > 0 && Object.keys(fileUploadDone).length === files.length) {
      setTimeout(() => {
        setModalResult("done");
      }, 1000);
    }
  }, [fileUploadDone]);

  const uploadedFiles: FileUploadStatus = {};
  const updateFileUploadStatus = (name: string, status: boolean) => {
    uploadedFiles[name] = status;
    setFileUploadDone({
      ...fileUploadDone,
      ...uploadedFiles,
    });
  };

  const uploadFileToSalesforce = (ticket: string, file: File) => {
    const reader = new FileReader();

    reader.onloadend = async () => {
      if (reader.result instanceof ArrayBuffer) {
        const base64Data = Buffer.from(reader.result).toString("base64");
        const attachmentData = {
          ParentId: ticket,
          Name: file.name,
          Body: base64Data,
          ContentType: file.type || "application/octet-stream",
        };

        core?.invokeIntegrationHubRequest("POST", "/salesforce/ticket/attachments", attachmentData).then((response) => {
          if (!(response?.statusCode === 200) && !(response?.statusCode === 201)) {
            const errorText = response?.body;
            console.error("File upload failed:", response?.statusCode, errorText);
            setTimeout(() => {
              updateFileUploadStatus(file.name, false);
            }, 1000 * files.indexOf(file));
          } else {
            setTimeout(() => {
              updateFileUploadStatus(file.name, true);
            }, 1000 * files.indexOf(file));
          }
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  if (mounted && integrationHubUser && fieldsLoaded) {
    return (
      <React.Fragment>
        <Modal
          open={modalOpen}
          onClose={() => {
            setModalOpen(true);
          }}
        >
          <ModalDialog>
            <Typography>
              {modalResult === "ticket"
                ? "Submitting Ticket"
                : modalResult === "upload"
                ? `Uploading Attachments ${Object.keys(fileUploadDone || {}).length}/${files.length}`
                : modalResult === "done"
                ? "Done!"
                : "Error"}
            </Typography>
            <Typography level="body-sm">
              {modalResult === "ticket"
                ? "Please don't close or refresh this page."
                : modalResult === "upload"
                ? "Please don't close or refresh this page."
                : modalResult === "done"
                ? "You can now safely close this window."
                : "There was an error submitting your ticket, please try again later."}
            </Typography>
            {modalResult === "error" && modalErrorMessage && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  // backgroundColor: 'danger.softBg',
                  borderRadius: "sm",
                  border: "1px solid",
                  borderColor: "danger.outlinedBorder",
                }}
              >
                <Typography
                  level="body-sm"
                  sx={{
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    // color: 'danger.plainColor
                  }}
                >
                  {modalErrorMessage}
                </Typography>
              </Box>
            )}
            {(modalResult === "done" || modalResult === "error") && (
              <Button
                onClick={() => {
                  location.reload();
                }}
                color={modalResult === "done" ? "success" : "danger"}
              >
                Ok
              </Button>
            )}
            {(modalResult === "ticket" || modalResult === "upload") && (
              <LinearProgress
                determinate={!(modalResult === "ticket")}
                value={modalResult === "ticket" ? undefined : modalResult === "upload" ? (Object.keys(fileUploadDone || {}).length * 100) / files.length : 100}
              ></LinearProgress>
            )}
          </ModalDialog>
        </Modal>
        <Typography key="title" level="title-lg">
          Service Team
        </Typography>
        <Box
          key={0}
          sx={{
            padding: 0,
            display: "grid",
            justifyContent: "space-between",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 2,
            mb: 2,
            "& > hr": {
              gridColumn: "1/-1",
            },
          }}
        >
          {Object.keys(fields).map((key) => {
            return (
              <Box
                key={`${key}_selector`}
                sx={{
                  cursor: "pointer",
                  gridColumn: {
                    xs: "span 6",
                    sm: "span 3",
                    lg: "span 2",
                  },
                  display: "flex",
                  alignItems: "stretch",
                }}
                onClick={() => {
                  setFieldValid({});
                  setFieldData({});
                  setDynamicFieldSelection({});
                  setSelectedServiceTeam(key);
                }}
              >
                <Card
                  variant={selectedServiceTeam === key ? "solid" : "soft"}
                  sx={{
                    flexGrow: 1,
                  }}
                  color={selectedServiceTeam === key ? "primary" : undefined}
                >
                  <CardContent>
                    <Typography textColor="inherit" level="title-lg">
                      {key}
                    </Typography>
                    <Typography level="body-sm" textColor="inherit">
                      {fields[key].description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>

        <Card
          id={`${selectedServiceTeam}_form`}
          sx={{
            mt: 2,
          }}
        >
          {selectedServiceTeam && (
            <Box sx={{ mb: 1 }}>
              <Typography level="title-md">Required Information</Typography>
              <Typography level="body-sm">
                Please complete all the fields below to submit a new ticket. It's recommended to do so in order as some fields are dynamic.
              </Typography>
            </Box>
          )}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              setModalOpen(true);
              if (core) {
                core
                  .invokeIntegrationHubRequest("POST", `/salesforce/tickets`, {
                    fields: {
                      ...fieldData,
                    },
                  })
                  .then((resp) => {
                    // uploadFileToSalesforce

                    if (resp.statusCode === 200 || resp.statusCode === 201) {
                      // Check for related object errors
                      const responseBody = resp.body as any;
                      const relatedObjectErrors = responseBody.relatedObjects?.filter((obj: any) => obj.error) || [];

                      if (relatedObjectErrors.length > 0) {
                        // Some related objects failed, but main ticket was created
                        const errorMessages = relatedObjectErrors.map((obj: any) => `${obj.objectName}: ${obj.error}`).join("\n");
                        const warningMessage =
                          process.env.NODE_ENV === "development"
                            ? `Ticket created successfully, but some related data failed:\n\n${errorMessages}`
                            : `Ticket created successfully, but some related information could not be saved. Please contact support if needed.`;

                        setModalErrorMessage(warningMessage);
                        setModalResult("error");
                        setModalOpen(true);
                      } else {
                        // Everything succeeded
                        if (files.length > 0) {
                          setModalResult("upload");
                          for (const file of files) {
                            uploadFileToSalesforce(responseBody.case?.id || responseBody.id || "", file);
                          }
                        } else {
                          setModalResult("done");
                          setModalOpen(true);
                        }
                      }
                    } else {
                      const errorMessage =
                        process.env.NODE_ENV === "development"
                          ? `API Error (${resp.statusCode}): ${(resp.body as any)[0]?.message || JSON.stringify(resp.body, null, 2)}`
                          : (resp.body as any)[0]?.message || "An error occurred while submitting your ticket.";
                      setModalErrorMessage(errorMessage);
                      setModalResult("error");
                      setModalOpen(true);
                    }
                  })
                  .catch((err) => {
                    console.error(err);
                    const errorMessage =
                      process.env.NODE_ENV === "development"
                        ? `Development Error: ${err?.message || err?.toString() || "Unknown error"}`
                        : "An unexpected error occurred. Please try again later.";
                    setModalErrorMessage(errorMessage);
                    setModalResult("error");
                    setModalOpen(true);
                  });
              }
            }}
          >
            <Box
              key={0}
              sx={{
                padding: 0,
                display: "grid",
                justifyContent: "space-between",
                gridTemplateColumns: {
                  xs: "100%",
                  sm: "50% 50%",
                  lg: "repeat(auto-fit, 32%) 32%",
                },
                mb: 2,
                "& > hr": {
                  gridColumn: "1/-1",
                },
              }}
            >
              {selectedServiceTeam &&
                selectedFields?.fields.map((field, idx) => (
                  <InputField
                    key={`${selectedServiceTeam}_${field.name}_${idx}`}
                    selectedServiceTeam={selectedServiceTeam}
                    dynamicFieldSelection={dynamicFieldSelection}
                    setDynamicFieldSelection={setDynamicFieldSelection}
                    selectedFields={selectedFields}
                    setSelectedFields={setSelectedFields}
                    field={field}
                    fieldData={fieldData}
                    setFieldData={setFieldData}
                    integrationHubUser={integrationHubUser}
                    fieldValid={fieldValid}
                    setFieldValid={setFieldValid}
                    allFieldsData={getAllFields(selectedFields.fields)}
                  />
                ))}
              {!selectedServiceTeam && (
                <Box>
                  <Typography startDecorator={<InfoIcon />}>Please select a service team to begin.</Typography>
                </Box>
              )}
            </Box>
            {selectedServiceTeam && (
              <>
                <Stack spacing={2} sx={{ my: 1 }}>
                  <DropZone files={files} setFiles={setFiles} />
                </Stack>
                <CardOverflow sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                  <CardActions sx={{ alignSelf: "flex-end", pt: 2 }}>
                    {selectedServiceTeam ? (
                      <Button type="submit" onClick={() => {}} startDecorator={<SendIcon></SendIcon>}>
                        Submit
                      </Button>
                    ) : (
                      ""
                    )}
                  </CardActions>
                </CardOverflow>
              </>
            )}
          </form>
        </Card>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Typography>{!fieldsLoaded ? "Loading field configurations..." : "Please wait while we check your account..."}</Typography>
      <Box>
        <LinearProgress></LinearProgress>
      </Box>
    </React.Fragment>
  );
}
